import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { getImageUrl, authAPI } from '../services/api';

// FIX: Layout component provides the main app shell with sidebar navigation,
// top header with sucursal selector, user menu, and responsive mobile support.
const Layout = () => {
  const { config } = useApp();
  const { usuario, sucursales, sucursalActiva, modoEspectador, cambiarSucursal, logout, cerrarTodasSesiones, esAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ actual: '', nuevo: '', confirmar: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Cerrar sidebar en móvil al cambiar de ruta
  useEffect(() => {
    setMobileOpen(false);
    setShowUserMenu(false);
  }, [location]);

  // Guardar preferencia de colapso
  useEffect(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved) setCollapsed(saved === 'true');
  }, []);

  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebar_collapsed', newState.toString());
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleCerrarTodasSesiones = async () => {
    if (window.confirm('¿Cerrar TODAS las sesiones de esta cuenta?\n\nTodos los dispositivos que usen esta cuenta serán desconectados, incluyendo el tuyo.')) {
      await cerrarTodasSesiones();
      navigate('/login', { replace: true });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.nuevo.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordForm.nuevo !== passwordForm.confirmar) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.cambiarPassword(passwordForm.actual, passwordForm.nuevo);
      setPasswordSuccess('Contraseña actualizada correctamente');
      setPasswordForm({ actual: '', nuevo: '', confirmar: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Error al cambiar la contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  const openPasswordModal = () => {
    setShowUserMenu(false);
    setPasswordForm({ actual: '', nuevo: '', confirmar: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswordModal(true);
  };

  const getPageTitle = () => {
    const titles = {
      '/': 'Dashboard', '/productos': 'Productos', '/categorias': 'Categorías',
      '/marcas': 'Marcas', '/proveedores': 'Proveedores', '/compras': 'Compras',
      '/inventario': 'Inventario', '/configuracion': 'Configuración',
      '/clientes': 'Clientes', '/historias-clinicas': 'Historias Clínicas', '/historia-clinica/nueva': 'Nueva Historia Clínica',
      '/ventas': 'Punto de Venta', '/caja': 'Gestión de Caja', '/gastos': 'Gastos / Egresos', '/gastos-diarios': 'Gastos Diarios', '/reportes': 'Centro de Reportes',
      '/ordenes-trabajo': 'Órdenes de Trabajo',
      '/facturacion': 'Facturacion Electronica',
      '/transferencias': 'Transferencias entre Sucursales',
      '/analisis-ia': 'Análisis con IA',
      '/conciliacion-bancaria': 'Conciliación Bancaria',
      '/manual': 'Manual de Usuario',
      '/sucursales': 'Gestión de Sucursales', '/usuarios': 'Gestión de Usuarios',
      '/api-docs': 'API Pública'
    };
    if (location.pathname.startsWith('/kardex')) return 'Kardex';
    if (location.pathname.startsWith('/historia-clinica/') && location.pathname !== '/historia-clinica/nueva') return 'Editar Historia Clínica';
    return titles[location.pathname] || 'Dashboard';
  };

  const menuItems = [
    // ─── INICIO ───
    { path: '/', icon: 'bi-speedometer2', label: 'Dashboard', permiso: 'dashboard' },

    // ─── PACIENTES (gestión clínica: pacientes, HC, OT, catálogo de servicios médicos) ───
    { section: 'PACIENTES', permisos: ['clientes', 'historia_clinica', 'ordenes_trabajo', 'servicios', 'certificados'] },
    { path: '/clientes', icon: 'bi-people-fill', label: 'Pacientes / Clientes', permiso: 'clientes' },
    { path: '/historias-clinicas', icon: 'bi-clipboard2-pulse-fill', label: 'Historias Clínicas', permiso: 'historia_clinica' },
    { path: '/certificados', icon: 'bi-file-earmark-medical-fill', label: 'Certificados', permiso: 'certificados' },
    { path: '/ordenes-trabajo', icon: 'bi-eyeglasses', label: 'Órdenes de Trabajo', permiso: 'ordenes_trabajo' },
    { path: '/servicios-catalogo', icon: 'bi-heart-pulse', label: 'Catálogo de Servicios', permiso: 'servicios' },

    // ─── PUNTO DE VENTA (operación diaria del cajero, flujo natural) ───
    { section: 'PUNTO DE VENTA', permisos: ['ventas', 'caja', 'facturacion'] },
    { path: '/ventas', icon: 'bi-cart-check-fill', label: 'Nueva Venta', permiso: 'ventas' },
    { path: '/caja', icon: 'bi-safe', label: 'Caja Registradora', permiso: 'caja' },
    { path: '/cuentas-por-cobrar', icon: 'bi-wallet2', label: 'Cuentas por Cobrar', permiso: 'ventas' },
    { path: '/gastos', icon: 'bi-receipt', label: 'Gastos / Egresos', permiso: 'caja' },
    { path: '/gastos-diarios', icon: 'bi-journal-text', label: 'Gastos Diarios', permiso: 'admin_gerente' },
    { path: '/facturacion', icon: 'bi-file-earmark-check-fill', label: 'Facturación SRI', permiso: 'facturacion' },

    // ─── INVENTARIO (productos físicos) ───
    { section: 'INVENTARIO', permisos: ['productos', 'compras', 'inventario', 'transferencias'] },
    { path: '/productos', icon: 'bi-box-seam-fill', label: 'Productos', permiso: 'productos' },
    { path: '/compras', icon: 'bi-cart-plus-fill', label: 'Compras', permiso: 'compras' },
    { path: '/inventario', icon: 'bi-clipboard-data-fill', label: 'Stock / Kardex', permiso: 'inventario' },
    { path: '/transferencias', icon: 'bi-arrow-left-right', label: 'Transferencias', permiso: 'transferencias' },

    // ─── REPORTES Y ANÁLISIS (vistas analíticas, conciliación, IA) ───
    { section: 'REPORTES Y ANÁLISIS', permisos: ['ventas', 'caja', 'configuracion'] },
    { path: '/reportes', icon: 'bi-file-earmark-bar-graph', label: 'Centro de Reportes', permiso: 'ventas' },
    { path: '/actividad-equipo', icon: 'bi-people-fill', label: 'Actividad del Equipo', permiso: 'admin_only' },
    { path: '/conciliacion-bancaria', icon: 'bi-bank2', label: 'Conciliación Bancaria', permiso: 'configuracion' },
    { path: '/analisis-ia', icon: 'bi-robot', label: 'Análisis con IA', permiso: 'admin_only' },

    // ─── CATÁLOGOS (datos maestros de productos) ───
    { section: 'CATÁLOGOS', permisos: ['categorias', 'marcas', 'proveedores'] },
    { path: '/categorias', icon: 'bi-tags-fill', label: 'Categorías', permiso: 'categorias' },
    { path: '/marcas', icon: 'bi-bookmark-star-fill', label: 'Marcas', permiso: 'marcas' },
    { path: '/proveedores', icon: 'bi-truck', label: 'Proveedores', permiso: 'proveedores' },

    // ─── ADMINISTRACIÓN (configuración del sistema) ───
    { section: 'ADMINISTRACIÓN', permisos: ['configuracion', 'usuarios', 'sucursales', 'api_docs'] },
    { path: '/sucursales', icon: 'bi-shop-window', label: 'Sucursales', permiso: 'sucursales' },
    { path: '/usuarios', icon: 'bi-people-fill', label: 'Usuarios', permiso: 'usuarios' },
    { path: '/configuracion', icon: 'bi-gear-fill', label: 'Configuración', permiso: 'configuracion' },
    { path: '/api-docs', icon: 'bi-code-slash', label: 'API Pública', permiso: 'api_docs' },

    // ─── AYUDA ───
    { section: 'AYUDA', permisos: [] },
    { path: '/manual', icon: 'bi-book', label: 'Manual de Usuario' }
  ];

  // FIX: Permission check is memoized to avoid re-computing on every render.
  // Supports: no permiso (always visible), super_admin_only, admin_only,
  // wildcard '*', exact match, prefix match (permiso:sub), and base match.
  const tienePermiso = useCallback((permiso) => {
    if (!permiso) return true;
    if (permiso === 'super_admin_only') return usuario?.rol === 'super_admin';
    if (permiso === 'admin_only') return usuario?.rol === 'super_admin' || usuario?.rol === 'admin';
    // Igual que soloAdminOGerente del backend (gastos diarios)
    if (permiso === 'admin_gerente') return esAdmin() || usuario?.rol === 'gerente';
    if (esAdmin()) return true;
    const permisos = usuario?.permisos || [];
    if (permisos.includes('*')) return true;
    if (permisos.includes(permiso)) return true;
    if (permisos.some(p => p.startsWith(permiso + ':') || p === permiso)) return true;
    const base = permiso.split(':')[0];
    if (permisos.includes(base)) return true;
    return false;
  }, [usuario?.permisos, esAdmin]);

  // Obtener iniciales del usuario para avatar (memoizado)
  const initials = useMemo(() => {
    if (!usuario?.nombre) return 'U';
    const parts = usuario.nombre.split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }, [usuario?.nombre]);

  // FIX: Safe logo URL with fallback — getImageUrl may return null if config.logo_url is empty
  const logoUrl = useMemo(() => {
    try {
      return getImageUrl(config?.logo_url) || `${process.env.PUBLIC_URL}/logo-icon.png`;
    } catch {
      return `${process.env.PUBLIC_URL}/logo-icon.png`;
    }
  }, [config?.logo_url]);

  // Pre-filtrar items del menu (memoizado para evitar re-render innecesarios)
  const filteredMenuItems = useMemo(() => {
    return menuItems.map((item, idx) => {
      if (item.section) {
        const visible = !item.permisos || esAdmin() || item.permisos.some(p => tienePermiso(p));
        return { ...item, _idx: idx, _visible: visible };
      }
      return { ...item, _idx: idx, _visible: tienePermiso(item.permiso) };
    }).filter(item => item._visible);
  }, [usuario?.permisos, esAdmin, tienePermiso]);

  return (
    <div className="app-container">
      {/* Overlay para móvil */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            {/* FIX: onError fallback prevents broken image if logo URL fails to load */}
            <img
              src={logoUrl}
              alt="Logo"
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = `${process.env.PUBLIC_URL}/logo-icon.png`;
              }}
            />
          </div>
          {!collapsed && (
            <div className="sidebar-info">
              <h1 className="sidebar-title">{config.nombre_sistema || 'Oftalmetryc'}</h1>
              <p className="sidebar-subtitle">{config.empresa_nombre || 'Sistema Integral'}</p>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {filteredMenuItems.map((item) => {
            if (item.section) {
              return !collapsed ? <div key={item._idx} className="nav-section"><span className="nav-section-title">{item.section}</span></div> : null;
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                title={collapsed ? item.label : ''}
              >
                <i className={`bi ${item.icon}`}></i>
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <small>
              Desarrollado por{' '}
              <a href="https://www.keybidigital.com" target="_blank" rel="noopener noreferrer" style={{ color: '#8bb8e8', textDecoration: 'none', fontWeight: 600 }}>
                Keybidigital
              </a>
            </small>
          )}
          <button
            className="collapse-btn"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${collapsed ? 'expanded' : ''}`}>
        <header className="top-header">
          <div className="d-flex align-items-center gap-3">
            {/* FIX: Mobile hamburger menu — d-lg-none hides on desktop, shows on mobile/tablet */}
            <button
              className="btn btn-link d-lg-none p-0 text-dark"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menú de navegación"
            >
              <i className="bi bi-list fs-4"></i>
            </button>
            <h2 className="header-title mb-0">{getPageTitle()}</h2>
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* Selector de Sucursal - Solo para super_admin */}
            {sucursales.length > 1 && usuario?.rol === 'super_admin' && (
              <div className={`sucursal-selector d-none d-md-flex ${modoEspectador ? 'modo-espectador' : ''}`}
                style={modoEspectador ? { borderColor: '#ffc107', background: '#fff3cd' } : {}}
              >
                <i className={`bi ${modoEspectador ? 'bi-eye-fill text-warning' : 'bi-shop text-muted'}`}></i>
                {/* FIX: Full page navigation to '/' is intentional here.
                   Changing sucursal requires re-fetching ALL context data
                   (inventory, sales, cash register, clients, etc.) so a full
                   reload to Dashboard is the correct behavior. Using
                   navigate('/') would NOT re-fetch data from the server
                   for the new sucursal context. */}
                <select
                  value={sucursalActiva || ''}
                  onChange={(e) => {
                    cambiarSucursal(e.target.value);
                    // FIX: Navigate to root to re-fetch all data for new sucursal context.
                    // A full page reload is needed because all API calls depend on sucursalActiva.
                    window.location.href = '/';
                  }}
                >
                  {sucursales.map(suc => (
                    <option key={suc.id} value={suc.id}>
                      {suc.nombre} {suc.id === usuario?.sucursalId ? '(Tu sucursal)' : ''} {suc.es_matriz ? '- Matriz' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Info de sucursal actual - Para no-super_admin o si solo hay una */}
            {(usuario?.rol !== 'super_admin' || sucursales.length === 1) && (
              <span className="text-muted small d-none d-md-inline">
                <i className="bi bi-shop me-1"></i>
                {sucursales.find(s => s.id === sucursalActiva)?.nombre || sucursales[0]?.nombre}
              </span>
            )}

            {/* User Menu */}
            <div className="user-menu position-relative">
              <button
                className="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">{initials}</div>
                <div className="user-info d-none d-md-flex">
                  <span className="user-name">{usuario?.nombre || 'Usuario'}</span>
                  <span className="user-role">{usuario?.rol || 'Sin rol'}</span>
                </div>
                <i className="bi bi-chevron-down text-muted small"></i>
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100"
                    style={{ zIndex: 1050 }}
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div
                    className="position-absolute end-0 mt-2 bg-white rounded-3 shadow-lg border"
                    style={{ zIndex: 1051, minWidth: '200px', top: '100%' }}
                  >
                    <div className="p-3 border-bottom">
                      <div className="fw-bold">{usuario?.nombre}</div>
                      <div className="text-muted small">{usuario?.email}</div>
                      <span className="badge bg-primary bg-opacity-10 text-primary mt-1">
                        {usuario?.rol}
                      </span>
                    </div>
                    <div className="py-1">
                      <button
                        className="dropdown-item py-2 px-3 d-flex align-items-center gap-2"
                        onClick={openPasswordModal}
                      >
                        <i className="bi bi-key text-warning"></i>
                        Cambiar Contraseña
                      </button>
                      {esAdmin() && (
                        <button
                          className="dropdown-item py-2 px-3 d-flex align-items-center gap-2"
                          onClick={handleCerrarTodasSesiones}
                        >
                          <i className="bi bi-shield-lock text-danger"></i>
                          Cerrar Todas las Sesiones
                        </button>
                      )}
                      <hr className="my-1" />
                      <button
                        className="dropdown-item py-2 px-3 d-flex align-items-center gap-2"
                        onClick={handleLogout}
                      >
                        <i className="bi bi-box-arrow-right text-danger"></i>
                        Cerrar Sesión
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Banner Modo Espectador */}
        {modoEspectador && (
          <div style={{
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
            color: '#664d03',
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            fontWeight: 600,
            fontSize: '0.9rem',
            borderBottom: '2px solid #ffc107',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <i className="bi bi-eye-fill" style={{ fontSize: '1.1rem' }}></i>
            <span>
              Modo Espectador — {sucursales.find(s => s.id === sucursalActiva)?.nombre || 'Otra sucursal'}
            </span>
            <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>
              (Solo lectura — No puedes crear, editar ni eliminar datos)
            </span>
          </div>
        )}

        <div className="page-container">
          <Outlet />
        </div>
      </main>

      {/* Modal Cambiar Contraseña */}
      {showPasswordModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowPasswordModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title">
                    <i className="bi bi-key me-2 text-warning"></i>
                    Cambiar Contraseña
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowPasswordModal(false)} />
                </div>
                <form onSubmit={handlePasswordChange}>
                  <div className="modal-body pt-2">
                    {passwordError && (
                      <div className="alert alert-danger py-2 small">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        {passwordError}
                      </div>
                    )}
                    {passwordSuccess && (
                      <div className="alert alert-success py-2 small">
                        <i className="bi bi-check-circle me-1"></i>
                        {passwordSuccess}
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Contraseña Actual</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordForm.actual}
                        onChange={(e) => setPasswordForm({ ...passwordForm, actual: e.target.value })}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Nueva Contraseña</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordForm.nuevo}
                        onChange={(e) => setPasswordForm({ ...passwordForm, nuevo: e.target.value })}
                        required
                        minLength={6}
                      />
                      <div className="form-text">Mínimo 6 caracteres</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Confirmar Nueva Contraseña</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordForm.confirmar}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmar: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer border-0 pt-0">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setShowPasswordModal(false)}
                      disabled={savingPassword}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-warning"
                      disabled={savingPassword}
                    >
                      {savingPassword ? (
                        <><span className="spinner-border spinner-border-sm me-1"></span> Guardando...</>
                      ) : (
                        <><i className="bi bi-check-lg me-1"></i> Cambiar Contraseña</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default Layout;
